/* Nexlane — Customer Support assistant.
 *
 * Runs entirely in the browser. No backend, no API key, no third-party script.
 *
 * "Scanning the site" is two live Shopify endpoints:
 *   - /search/suggest.json  — product lookup. Returns titles, images and prices
 *     already converted into the visitor's market currency (verified: the same
 *     charger returns 16.99 for US, 14.95 for DE, 13.00 for GB), so the widget
 *     never has to convert money itself and can never show a stale price.
 *   - /collections.json     — the browse menu, cached for 6h in localStorage so
 *     new collections appear on their own without anyone editing this file.
 *
 * Everything degrades: if both fetches fail the assistant still answers the
 * delivery / returns / payment / tracking questions from local copy.
 */
(function () {
  'use strict';

  if (window.__nxSupportLoaded) return;
  window.__nxSupportLoaded = true;

  var CACHE_KEY = 'nx_support_collections_v1';
  var CACHE_TTL = 6 * 60 * 60 * 1000; // 6 hours
  var SUPPORT_EMAIL = 'beqaqatamadze8@gmail.com';

  var panel, bodyEl, inputEl, launcher, dotEl;
  var built = false;
  var collections = [];

  /* ------------------------------------------------------------------ utils */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function money(v) {
    if (v === null || v === undefined || v === '') return '';
    var raw = String(v).trim();
    // suggest.json returns a plain decimal in the visitor's market currency.
    var num = parseFloat(raw.replace(/[^0-9.\-]/g, ''));
    if (isNaN(num)) return raw;
    var code = 'USD';
    try {
      if (window.Shopify && Shopify.currency && Shopify.currency.active) code = Shopify.currency.active;
    } catch (e) { /* keep USD */ }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: code
      }).format(num);
    } catch (e) {
      return code + ' ' + num.toFixed(2);
    }
  }

  // suggest.json returns `featured_image` as an OBJECT ({url, width, ...}) and
  // `image` as a plain string. Reading featured_image directly yields the
  // literal "[object Object]" as the src, so unwrap both shapes.
  function imageOf(p) {
    var f = p && p.featured_image;
    if (typeof f === 'string' && f) return f;
    if (f && typeof f === 'object' && typeof f.url === 'string') return f.url;
    if (typeof p.image === 'string' && p.image) return p.image;
    if (p.image && typeof p.image === 'object' && typeof p.image.url === 'string') return p.image.url;
    return '';
  }

  function cleanUrl(u) {
    // suggest.json appends _pos/_psq/_ss tracking params; drop them for a tidy link.
    return String(u || '').split('?')[0];
  }

  function scrollDown() {
    if (bodyEl) bodyEl.scrollTop = bodyEl.scrollHeight;
  }

  /* -------------------------------------------------------------- rendering */

  function say(html, who) {
    var m = document.createElement('div');
    m.className = 'nx-msg nx-msg--' + (who || 'bot');
    m.innerHTML = html;
    bodyEl.appendChild(m);
    scrollDown();
    return m;
  }

  function typing() {
    var m = document.createElement('div');
    m.className = 'nx-msg nx-msg--bot';
    m.innerHTML = '<span class="nx-typing"><i></i><i></i><i></i></span>';
    bodyEl.appendChild(m);
    scrollDown();
    return m;
  }

  function chips(items) {
    var wrap = document.createElement('div');
    wrap.className = 'nx-chips';
    items.forEach(function (it) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'nx-chip';
      b.textContent = it.label;
      b.addEventListener('click', function () {
        send(it.send || it.label);
      });
      wrap.appendChild(b);
    });
    bodyEl.appendChild(wrap);
    scrollDown();
  }

  function cards(products) {
    var wrap = document.createElement('div');
    wrap.className = 'nx-cards';
    products.forEach(function (p) {
      var a = document.createElement('a');
      a.className = 'nx-card';
      a.href = cleanUrl(p.url);
      var img = imageOf(p);
      var price = money(p.price);
      var was = p.compare_at_price && parseFloat(p.compare_at_price) > parseFloat(p.price)
        ? '<span class="nx-card__was">' + esc(money(p.compare_at_price)) + '</span>' : '';
      a.innerHTML =
        (img ? '<img class="nx-card__img" src="' + esc(img) + '" alt="" loading="lazy" width="56" height="56">'
             : '<span class="nx-card__img"></span>') +
        '<span class="nx-card__body">' +
          '<span class="nx-card__title">' + esc(p.title) + '</span>' +
          (price ? '<span class="nx-card__price">' + esc(price) + was + '</span>' : '') +
        '</span>' +
        '<span class="nx-card__cta" aria-hidden="true">&rsaquo;</span>';
      wrap.appendChild(a);
    });
    bodyEl.appendChild(wrap);
    scrollDown();
  }

  /* ------------------------------------------------------ catalogue scanning */

  function scanCollections(force) {
    var now = Date.now();
    if (!force) {
      try {
        var raw = localStorage.getItem(CACHE_KEY);
        if (raw) {
          var c = JSON.parse(raw);
          if (c && c.at && (now - c.at) < CACHE_TTL && c.items && c.items.length) {
            collections = c.items;
            return Promise.resolve(collections);
          }
        }
      } catch (e) { /* cache unreadable — refetch */ }
    }
    return fetch('/collections.json?limit=50', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var list = (d && d.collections) || [];
        collections = list
          .filter(function (c) { return c.handle !== 'frontpage'; })
          .map(function (c) { return { title: c.title, handle: c.handle }; });
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ at: now, items: collections }));
        } catch (e) { /* private mode — fine, we just refetch next time */ }
        return collections;
      })
      .catch(function () { return collections; });
  }

  function searchProducts(q) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(q) +
      '&resources[type]=product&resources[limit]=4' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=title,product_type,variants.title,tag';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        try { return d.resources.results.products || []; } catch (e) { return []; }
      })
      .catch(function () { return []; });
  }

  /* ----------------------------------------------------------------- intents */

  var PERKS = 'Delivery is <strong>free worldwide</strong>, and buying 2 items takes 10% off automatically (3+ takes 15%).';

  function browseChips() {
    var items = collections.slice(0, 6).map(function (c) {
      return { label: c.title, send: c.title };
    });
    items.push({ label: 'Show me the deals', send: 'deals' });
    chips(items);
  }

  function answer(text) {
    var q = text.toLowerCase().trim();

    var is = function (words) {
      for (var i = 0; i < words.length; i++) if (q.indexOf(words[i]) !== -1) return true;
      return false;
    };

    if (is(['hello', 'hi ', 'hey', 'hallo', 'guten tag']) || q === 'hi') {
      say('Hi — happy to help. What are you shopping for?');
      browseChips();
      return;
    }

    if (is(['ship', 'delivery', 'deliver', 'versand', 'lieferung', 'how long', 'arrive'])) {
      say('<strong>Delivery is free on every order, worldwide</strong> — no minimum spend.<br><br>' +
          'Orders are dispatched in 1–3 business days, then delivery takes about 7–15 business days. ' +
          'You get a tracking link by email as soon as it ships.');
      chips([{ label: 'Start shopping', send: 'bestsellers' }, { label: 'Track my order', send: 'track' }]);
      return;
    }

    if (is(['return', 'refund', 'exchange', 'rückgabe', 'money back'])) {
      say('You have <strong>30 days</strong> from delivery to request a return — unused and in its original packaging.<br><br>' +
          'Refunds go back to the card or wallet you paid with. Email ' +
          '<a href="mailto:' + SUPPORT_EMAIL + '">' + SUPPORT_EMAIL + '</a> with your order number to start one.');
      return;
    }

    if (is(['pay', 'payment', 'card', 'visa', 'mastercard', 'apple pay', 'google pay', 'paypal', 'bezahl'])) {
      say('We take <strong>Visa, Mastercard, American Express, Maestro and UnionPay</strong>, plus ' +
          '<strong>Apple Pay, Google Pay and Shop Pay</strong>.<br><br>' +
          'Checkout runs on Shopify secure payment infrastructure — we never see your card details. ' +
          'Full detail on the <a href="/pages/how-to-pay">How to Pay</a> page.');
      return;
    }

    if (is(['track', 'where is my order', 'order status', 'sendung'])) {
      say('You can track it on our <a href="/pages/track-your-order">Track Your Order</a> page using the number in your dispatch email.<br><br>' +
          'International parcels often show no movement for a few days mid-transit — that is normal. ' +
          'If nothing changes for 10 business days, email <a href="mailto:' + SUPPORT_EMAIL + '">' + SUPPORT_EMAIL + '</a> and we will chase it.');
      return;
    }

    if (is(['duty', 'duties', 'customs', 'import tax', 'zoll', 'vat'])) {
      say('Import duties and local taxes are set by your own country and are not included in our prices — they are the recipient responsibility. ' +
          'We cannot predict them, so it is worth checking your country import rules if you are unsure.');
      return;
    }

    if (is(['discount', 'coupon', 'promo', 'voucher', 'deal', 'sale', 'cheap', 'offer', 'rabatt', 'angebot'])) {
      say('Two things run automatically, no code needed:<br><br>' +
          '<strong>Buy 2 — save 10%</strong><br><strong>Buy 3 or more — save 15%</strong><br><br>' +
          'They apply in your cart on their own. Delivery is free on top of that.');
      chips([{ label: 'Show discounted items', send: 'deals' }]);
      return;
    }

    if (is(['human', 'agent', 'someone', 'speak to', 'contact', 'email', 'phone', 'complain'])) {
      say('A person will pick this up — email <a href="mailto:' + SUPPORT_EMAIL + '">' + SUPPORT_EMAIL + '</a> ' +
          'or use the <a href="/pages/contact">contact page</a>. Include your order number if you have one and you will get a faster answer.');
      return;
    }

    if (q === 'deals') {
      say('Here is what is discounted right now:');
      window.setTimeout(function () {
        say('<a href="/collections/hot-deals">Browse all deals &rsaquo;</a>');
      }, 250);
      lookup('sale');
      return;
    }

    if (is(['gift', 'present', 'geschenk'])) {
      say('Good gift picks — these land well and none of them need setup:');
      lookup('power bank projector speaker');
      return;
    }

    // Anything else: treat it as a product query.
    lookup(text);
  }

  function lookup(q) {
    var t = typing();
    searchProducts(q).then(function (products) {
      t.remove();
      if (products.length) {
        say('Here is what matches:');
        cards(products);
        say(PERKS);
      } else {
        say('I could not find a match for that one. Here is what we do stock — have a look:');
        browseChips();
      }
    });
  }

  /* ------------------------------------------------------------------- shell */

  function send(text) {
    var v = String(text || '').trim();
    if (!v) return;
    say(esc(v), 'user');
    inputEl.value = '';
    window.setTimeout(function () { answer(v); }, 220);
  }

  function build() {
    if (built) return;
    built = true;

    panel = document.createElement('div');
    panel.className = 'nx-sup nx-sup-panel';
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Customer Support');
    panel.innerHTML =
      '<div class="nx-sup-head">' +
        '<div>' +
          '<div class="nx-sup-head__title">Customer Support</div>' +
          '<div class="nx-sup-head__sub"><span class="nx-sup-head__live"></span>Usually replies instantly</div>' +
        '</div>' +
        '<button type="button" class="nx-sup-close" aria-label="Close support chat">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
        '</button>' +
      '</div>' +
      '<div class="nx-sup-body" aria-live="polite"></div>' +
      '<form class="nx-sup-form">' +
        '<input class="nx-sup-input" type="text" autocomplete="off" placeholder="Ask about a product, delivery…" aria-label="Type your message">' +
        '<button type="submit" class="nx-sup-send" aria-label="Send message">' +
          '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m22 2-7 20-4-9-9-4Z"/><path d="M22 2 11 13"/></svg>' +
        '</button>' +
      '</form>' +
      '<div class="nx-sup-foot">Automated assistant · a person answers at ' + esc(SUPPORT_EMAIL) + '</div>';

    document.body.appendChild(panel);

    bodyEl = panel.querySelector('.nx-sup-body');
    inputEl = panel.querySelector('.nx-sup-input');

    panel.querySelector('.nx-sup-close').addEventListener('click', close);
    panel.querySelector('.nx-sup-form').addEventListener('submit', function (e) {
      e.preventDefault();
      send(inputEl.value);
    });

    scanCollections(false).then(function () {
      say('Hi — I am the Nexlane assistant. I can help you find the right thing, or answer questions on delivery, payment and returns.');
      say(PERKS);
      browseChips();
    });
  }

  function open() {
    build();
    panel.classList.add('is-open');
    launcher.setAttribute('aria-expanded', 'true');
    if (dotEl) { dotEl.remove(); dotEl = null; }
    try { sessionStorage.setItem('nx_support_seen', '1'); } catch (e) {}
    // Refresh the catalogue in the background if the cache has gone stale.
    scanCollections(false);
    window.setTimeout(function () {
      if (window.innerWidth > 749 && inputEl) inputEl.focus();
    }, 420);
  }

  function close() {
    if (panel) panel.classList.remove('is-open');
    launcher.setAttribute('aria-expanded', 'false');
    launcher.focus();
  }

  function toggle() {
    if (panel && panel.classList.contains('is-open')) close(); else open();
  }

  function mount() {
    launcher = document.createElement('button');
    launcher.type = 'button';
    launcher.className = 'nx-sup nx-sup-launcher';
    launcher.setAttribute('aria-expanded', 'false');
    launcher.setAttribute('aria-haspopup', 'dialog');
    launcher.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z"/></svg>' +
      '<span class="nx-sup-launcher__label">Customer Support</span>';
    launcher.addEventListener('click', toggle);
    document.body.appendChild(launcher);

    // Keep clear of the mobile sticky add-to-cart bar if the theme renders one.
    if (document.querySelector('sticky-add-to-cart, .sticky-add-to-cart')) {
      document.documentElement.style.setProperty('--nx-sup-bottom', '86px');
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel && panel.classList.contains('is-open')) close();
    });

    // One gentle nudge per session, never repeated.
    var seen = false;
    try { seen = sessionStorage.getItem('nx_support_seen') === '1'; } catch (e) {}
    if (!seen) {
      window.setTimeout(function () {
        if (panel && panel.classList.contains('is-open')) return;
        dotEl = document.createElement('span');
        dotEl.className = 'nx-sup-launcher__dot';
        launcher.appendChild(dotEl);
      }, 30000);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }

  // Manual hook: NexlaneSupport.refresh() forces an immediate catalogue re-scan.
  window.NexlaneSupport = {
    open: function () { open(); },
    close: function () { close(); },
    refresh: function () { return scanCollections(true); }
  };
})();